import type { SupplierProductGetResponseChildType } from "@sellar-io/api-v2/types";
import { useMediaQuery } from "usehooks-ts";
import { useState } from "react";
import { Icon, SpinnerLegacy } from "../../../../../../../components";
import { Alert } from "../../../../../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
} from "../../../../../../../components/modal/Dialog";
import { usePriceTierList } from "../../../../../../../hooks/api/v2/priceTier/usePriceTierList";
import { ProductCategoryItemPriceRow } from "./ProductCategoryItemPriceRow";
import {
  BestPricePromise,
  BestPricePromiseFullSize,
  BestPricePromiseCompact,
} from "../../../../../../../components/bestPricePromise/BestPricePromise";
import { useRemoteFeatureFlags } from "../../../../../../../hooks/useRemoteFeatureFlags";
import { formatCurrency } from "@sellar-io/common";
import { Anchor, Button } from "@sellar-io/components";
import { useLocation, useNavigate } from "react-router";
import * as ROUTES from "../../../../../../../constants/routes";
import { twMerge } from "tailwind-merge";

export const ProductCategoryItemPriceTabContent = ({
  product,
}: {
  product: SupplierProductGetResponseChildType;
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const priceTierListQuery = usePriceTierList({
    limit: 50,
    query: [
      {
        type: "ProductId",
        modifier: "EQUALS",
        value: product.id,
      },
    ],
  });

  const unbundlingRelease2BEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2B",
  );

  const [visibleModalActive, setVisibleModalActive] = useState(false);
  const [discountableModalActive, setDiscountableModalActive] = useState(false);
  const [unitPriceModalActive, setUnitPriceModalActive] = useState(false);

  if (priceTierListQuery.isLoading) return <SpinnerLegacy />;

  if (priceTierListQuery.isError || !priceTierListQuery.isSuccess)
    return <div>Something went wrong.</div>;

  return (
    <div className={"flex flex-col gap-4 pt-4"}>
      {!unbundlingRelease2BEnabled && (
        <div className="flex justify-end">
          <BestPricePromise
            onClick={() =>
              window.open("https://sellar.io/supplier-house-rules", "_blank")
            }
          >
            {isMobile ? (
              <BestPricePromiseCompact />
            ) : (
              <BestPricePromiseFullSize />
            )}
          </BestPricePromise>
        </div>
      )}

      {product._count.ProductPriceTiers === 0 && (
        <Alert
          className={"w-full"}
          title={"Pricing missing"}
          message={
            "Add pricing for at least one of your customer groups to list this product on your storefront."
          }
          type={"warning"}
        />
      )}
      {unbundlingRelease2BEnabled && (
        <Alert
          type="information"
          title="Configure your product's pricing in the Pricing tab"
          message={
            <p>
              Everything pricing related is now handled in one place. For more
              information or help setting up your pricing, visit our{" "}
              <Anchor
                className="inline-flex items-center gap-1 text-blue-600 underline"
                href="https://intercom.help/sellario/en/articles/10148283-supplier-setup-pricing-and-discounts"
                target="_blank"
                rel="noopener noreferrer"
              >
                help centre
                <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
              </Anchor>
            </p>
          }
        />
      )}
      <div className={"overflow-x-auto rounded-lg border border-zinc-300"}>
        {unbundlingRelease2BEnabled ? (
          <table className={"w-full table-auto text-sm"}>
            <thead className={"text-left"}>
              <tr className={"border-b border-zinc-300 bg-zinc-50"}>
                <th
                  className={
                    "w-1/2 border-r border-zinc-300 bg-zinc-50 px-4 py-2"
                  }
                >
                  Price list
                </th>
                <th className={"w-1/2 px-4 py-2"}>
                  <div
                    className={"flex cursor-pointer items-center gap-2"}
                    onClick={() => setUnitPriceModalActive(true)}
                  >
                    Unit price (ex. VAT)
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {priceTierListQuery.data.pages.map((page) =>
                page
                  .filter((priceList) => priceList.derivationType === "base")
                  .map((priceList) => (
                    <NewPricingRow
                      key={priceList.id}
                      id={priceList.id}
                      productId={product.id}
                      name={priceList.name}
                      price={priceList.ProductPriceTiers[0]?.price ?? 0}
                      className="font-semibold"
                    />
                  )),
              )}
              {priceTierListQuery.data.pages.map((page) =>
                page
                  .filter(
                    (priceList) =>
                      priceList.derivationType !== "base" &&
                      priceList.BasePriceTierId,
                  )
                  .map((priceList) => (
                    <NewPricingRow
                      key={priceList.id}
                      id={priceList.id}
                      productId={product.id}
                      name={priceList.name}
                      price={priceList.ProductPriceTiers[0]?.price ?? 0}
                    />
                  )),
              )}
            </tbody>
          </table>
        ) : (
          <table
            className={"w-full table-auto border-separate border-spacing-0"}
          >
            <thead className={"text-left"}>
              <tr
                className={"border-b border-solid border-gray-200 bg-gray-50"}
              >
                <th
                  className={
                    "sticky left-0 z-10 border-r border-solid bg-gray-50 px-4 py-1"
                  }
                >
                  Price tier
                </th>
                <th className={"px-4 py-1"}>
                  <div
                    className={"flex cursor-pointer items-center gap-2"}
                    onClick={() => setDiscountableModalActive(true)}
                  >
                    Discountable{" "}
                    <Icon size={0.6} icon={"INFORMATION_OUTLINE"} />
                  </div>
                </th>
                <th className={"w-2/12 px-4 py-1"}>
                  <div
                    className={"flex cursor-pointer items-center gap-2"}
                    onClick={() => setUnitPriceModalActive(true)}
                  >
                    Unit price <Icon size={0.6} icon={"INFORMATION_OUTLINE"} />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className={"divide-y"}>
              {priceTierListQuery.data.pages.map((page) =>
                page.map((priceTier) => (
                  <ProductCategoryItemPriceRow
                    key={priceTier.id}
                    priceTier={priceTier}
                    product={product}
                  />
                )),
              )}
            </tbody>
          </table>
        )}
      </div>
      <Dialog
        active={visibleModalActive}
        onDismiss={() => setVisibleModalActive(false)}
      >
        <DialogNavigation
          leftIcon={"CHEVRON_DOWN"}
          title={"About 'Visible'"}
          onClickLeftIcon={() => setVisibleModalActive(false)}
          description={
            "Toggle this option to make a product either available or hidden for each of your customer groups."
          }
        />
      </Dialog>
      <Dialog
        active={discountableModalActive}
        onDismiss={() => setDiscountableModalActive(false)}
      >
        <DialogNavigation
          leftIcon={"CHEVRON_DOWN"}
          title={"About 'Discountable'"}
          onClickLeftIcon={() => setDiscountableModalActive(false)}
          description={
            "Toggle this option to make a product either eligible or ineligible for volume discounts for each of your price tiers"
          }
        />
      </Dialog>
      <Dialog
        active={unitPriceModalActive}
        onDismiss={() => setUnitPriceModalActive(false)}
      >
        <DialogNavigation
          leftIcon={"CHEVRON_DOWN"}
          title={"About 'Unit price'"}
          onClickLeftIcon={() => setUnitPriceModalActive(false)}
          description={
            "The price you enter here excludes VAT. VAT will be calculated for your customers whilst they are placing their orders"
          }
        />
      </Dialog>
    </div>
  );
};

const NewPricingRow = ({
  id,
  productId,
  name,
  price,
  className,
}: {
  id: number;
  productId: number;
  name: string;
  price: number;
  className?: string;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <tr className={twMerge("border-b border-zinc-300", className)}>
      <td className="border-r border-zinc-300 px-4 py-2">{name}</td>
      <td className="flex items-center justify-between px-4 py-2">
        {formatCurrency(price)}
        <Button
          size={"small"}
          color={"transparent"}
          className="p-1"
          onClick={() =>
            navigate(
              `${ROUTES.PRICE_LIST.href({ priceListId: id })}?productId=${productId}`,
              {
                state: { returnUrl: location.pathname + location.search },
              },
            )
          }
        >
          <Icon icon={"PENCIL"} className="text-black" size={0.7} />
        </Button>
      </td>
    </tr>
  );
};
